import { Component, lazy, Suspense, useEffect } from 'react'
import { Route, Routes, useLocation } from 'react-router-dom'
import { PageLoader } from './components/feedback/States.jsx'
import { DashboardLayout } from './components/layout/DashboardLayout.jsx'
import { PublicLayout } from './components/layout/PublicLayout.jsx'
import { ProtectedRoute, PublicOnlyRoute } from './features/auth/ProtectedRoute.jsx'

class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    console.error('App render error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-ink-950 px-4 text-center">
          <div className="surface-card max-w-md p-6">
            <h2 className="text-lg font-bold text-white">Terjadi Kendala Memuat Halaman</h2>
            <p className="mt-2 text-sm text-copy-muted">Halaman mengalami kendala saat merender data. Silakan muat ulang halaman.</p>
            <div className="mt-4 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => { this.setState({ hasError: false, error: null }); window.location.reload() }}
                className="button-base button-primary w-full"
              >
                Muat Ulang Halaman
              </button>
              <button
                type="button"
                onClick={() => { this.setState({ hasError: false, error: null }); window.location.href = '/' }}
                className="button-base button-secondary w-full"
              >
                Kembali ke Beranda
              </button>
            </div>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

const LandingPage = lazy(() => import('./pages/public/LandingPage.jsx'))
const ExplorePage = lazy(() => import('./pages/public/ExplorePage.jsx'))
const CampaignDetailPage = lazy(() => import('./pages/public/CampaignDetailPage.jsx'))
const TermsPage = lazy(() => import('./pages/public/TermsPage.jsx'))
const LoginPage = lazy(() => import('./pages/auth/LoginPage.jsx'))
const RoleSelectionPage = lazy(() => import('./pages/auth/RoleSelectionPage.jsx'))
const OnboardingPage = lazy(() => import('./pages/auth/OnboardingPage.jsx'))

const CreatorDashboardPage = lazy(() => import('./pages/creator/CreatorDashboardPage.jsx'))
const CreatorJobsPage = lazy(() => import('./pages/creator/CreatorJobsPage.jsx'))
const CreatorJobDetailPage = lazy(() => import('./pages/creator/CreatorJobDetailPage.jsx'))
const CreatorWalletPage = lazy(() => import('./pages/creator/CreatorWalletPage.jsx'))
const WithdrawalPage = lazy(() => import('./pages/creator/WithdrawalPage.jsx'))
const CreatorProfilePage = lazy(() => import('./pages/creator/CreatorProfilePage.jsx'))

const BrandDashboardPage = lazy(() => import('./pages/brand/BrandDashboardPage.jsx'))
const BrandCampaignsPage = lazy(() => import('./pages/brand/BrandCampaignsPage.jsx'))
const CampaignCreatePage = lazy(() => import('./pages/brand/CampaignCreatePage.jsx'))
const BrandApplicationsPage = lazy(() => import('./pages/brand/BrandApplicationsPage.jsx'))
const BrandFinancePage = lazy(() => import('./pages/brand/BrandFinancePage.jsx'))
const BrandProfilePage = lazy(() => import('./pages/brand/BrandProfilePage.jsx'))

const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage.jsx'))
const AdminCampaignsPage = lazy(() => import('./pages/admin/AdminCampaignsPage.jsx'))
const AdminReviewsPage = lazy(() => import('./pages/admin/AdminReviewsPage.jsx'))
const AdminCreatorReviewsPage = lazy(() => import('./pages/admin/AdminCreatorReviewsPage.jsx'))
const AdminSubmissionsPage = lazy(() => import('./pages/admin/AdminSubmissionsPage.jsx'))
const AdminWithdrawalsPage = lazy(() => import('./pages/admin/AdminWithdrawalsPage.jsx'))
const AdminApplicationsPage = lazy(() => import('./pages/admin/AdminApplicationsPage.jsx'))
const AdminResourcePage = lazy(() => import('./pages/admin/AdminResourcePage.jsx'))
const AdminCategoriesPage = lazy(() => import('./pages/admin/AdminCategoriesPage.jsx'))
const AdminBankAccountsPage = lazy(() => import('./pages/admin/AdminBankAccountsPage.jsx'))
const AdminBrandWalletsPage = lazy(() => import('./pages/admin/AdminBrandWalletsPage.jsx'))
const AdminSettingsPage = lazy(() => import('./pages/admin/AdminSettingsPage.jsx'))
const AdminSuperAdminPage = lazy(() => import('./pages/admin/AdminSuperAdminPage.jsx'))
const AdminRolesPage = lazy(() => import('./pages/admin/AdminRolesPage.jsx'))
const AdminReportsPage = lazy(() => import('./pages/admin/AdminReportsPage.jsx'))
const AdminBroadcastsPage = lazy(() => import('./pages/admin/AdminBroadcastsPage.jsx'))
const AdminNewBroadcastPage = lazy(() => import('./pages/admin/AdminNewBroadcastPage.jsx'))

import { NotFoundPage, SuspendedPage, UnauthorizedPage } from './pages/public/StatusPages.jsx'

function ScrollManager() {
  const { pathname, hash } = useLocation()
  useEffect(() => {
    if (hash) {
      window.requestAnimationFrame(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth' }))
    } else window.scrollTo({ top: 0, behavior: 'instant' })
  }, [hash, pathname])
  return null
}

export function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <ScrollManager />
        <Routes>
          <Route element={<PublicLayout />}>
            <Route index element={<LandingPage />} />
            <Route path="explore" element={<ExplorePage />} />
            <Route path="campaign/:publicId" element={<CampaignDetailPage />} />
            <Route path="campaigns/:publicId" element={<CampaignDetailPage />} />
            <Route path="terms" element={<TermsPage />} />
          </Route>

          <Route element={<PublicOnlyRoute />}>
            <Route path="login" element={<LoginPage />} />
          </Route>
          <Route path="choose-role" element={<RoleSelectionPage />} />
          <Route path="onboarding" element={<OnboardingPage />} />

          <Route element={<ProtectedRoute roles={['CREATOR']} />}>
            <Route element={<DashboardLayout />}>
              <Route path="creator" element={<CreatorDashboardPage />} />
              <Route path="creator/jobs" element={<CreatorJobsPage />} />
              <Route path="creator/jobs/:applicationId" element={<CreatorJobDetailPage />} />
              <Route path="creator/wallet" element={<CreatorWalletPage />} />
              <Route path="creator/withdraw" element={<WithdrawalPage />} />
              <Route path="creator/profile" element={<CreatorProfilePage />} />
            </Route>
          </Route>

          <Route element={<ProtectedRoute roles={['BRAND']} />}>
            <Route element={<DashboardLayout />}>
              <Route path="brand" element={<BrandDashboardPage />} />
              <Route path="brand/campaigns" element={<BrandCampaignsPage />} />
              <Route path="brand/campaigns/new" element={<CampaignCreatePage />} />
              <Route path="brand/campaigns/:publicId/edit" element={<CampaignCreatePage />} />
              <Route path="brand/applications" element={<BrandApplicationsPage />} />
              <Route path="brand/finance" element={<BrandFinancePage />} />
              <Route path="brand/profile" element={<BrandProfilePage />} />
            </Route>
          </Route>

          <Route element={<ProtectedRoute roles={['ADMIN', 'SUPERADMIN']} />}>
            <Route element={<DashboardLayout />}>
              <Route path="admin" element={<AdminDashboardPage />} />
              <Route path="admin/reports" element={<AdminReportsPage />} />
              <Route path="admin/brands" element={<AdminResourcePage resource="brands" />} />
              <Route path="admin/creator-reviews" element={<AdminCreatorReviewsPage />} />
              <Route path="admin/creators" element={<AdminResourcePage resource="creators" />} />
              <Route path="admin/campaigns" element={<AdminCampaignsPage />} />
              <Route path="admin/reviews" element={<AdminReviewsPage />} />
              <Route path="admin/applications" element={<AdminApplicationsPage />} />
              <Route path="admin/submissions" element={<AdminSubmissionsPage />} />
              <Route path="admin/validations" element={<AdminSubmissionsPage validationOnly />} />
              <Route path="admin/earnings" element={<AdminResourcePage resource="earnings" />} />
              <Route path="admin/withdrawals" element={<AdminWithdrawalsPage />} />
              <Route path="admin/ledger" element={<AdminResourcePage resource="ledger" />} />
              <Route path="admin/bank-accounts" element={<AdminBankAccountsPage />} />
              <Route path="admin/brand-wallets" element={<AdminBrandWalletsPage />} />
              <Route path="admin/audit" element={<AdminResourcePage resource="audit" />} />
              <Route path="admin/categories" element={<AdminCategoriesPage />} />
              <Route path="admin/settings" element={<AdminSettingsPage />} />
              <Route path="admin/broadcasts" element={<AdminBroadcastsPage />} />
              <Route path="admin/broadcasts/new" element={<AdminNewBroadcastPage />} />
              <Route path="admin/superadmin" element={<AdminSuperAdminPage />} />
              <Route path="admin/roles" element={<AdminRolesPage />} />
            </Route>
          </Route>

          <Route path="unauthorized" element={<UnauthorizedPage />} />
          <Route path="account-suspended" element={<SuspendedPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  )
}
